import { useCallback, useRef, useState, memo, forwardRef } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Slot } from '@radix-ui/react-slot';

import { useMergedRefs } from '@page/core/hooks/common';
import { cn } from '../../lib/styles';
import { TooltipContent, TooltipProvider } from '../../designSystem/Tooltip';

export interface EllipsisTooltipProps extends React.ComponentPropsWithoutRef<'span'> {
  children?: React.ReactNode;
  /** Tooltip content */
  label?: React.ReactNode;
  delayDuration?: number;
  /** Number of lines to show before truncating. Defaults to 1 (single line) */
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;
  side?: TooltipPrimitive.TooltipContentProps['side'];
  sideOffset?: number;
  align?: TooltipPrimitive.TooltipContentProps['align'];
  withinPortal?: boolean;
  asChild?: boolean;
  element?: string;
}

const lineClampClasses = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
} as const;

const EllipsisTooltipBase = forwardRef<HTMLSpanElement, EllipsisTooltipProps>(
  (
    {
      children,
      label,
      delayDuration = 250,
      className,
      lineClamp = 1,
      side = 'top',
      sideOffset = 4,
      align,
      withinPortal = true,
      asChild = false,
      element = 'span',
      ...rest
    },
    forwardedRef,
  ) => {
    const internalRef = useRef<HTMLElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const mergedRef = useMergedRefs(forwardedRef, internalRef);

    const checkTruncation = useCallback((): boolean => {
      const element = internalRef.current;
      if (!element) return false;

      if (lineClamp === 1) {
        return element.scrollWidth > element.offsetWidth;
      }
      return element.scrollHeight > element.offsetHeight;
    }, [lineClamp]);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (open) {
          const isTruncated = checkTruncation();
          setIsOpen(isTruncated);
        } else {
          setIsOpen(false);
        }
      },
      [checkTruncation],
    );

    const truncationClass = lineClampClasses[lineClamp];
    const Component = asChild ? Slot : element;

    return (
      <TooltipProvider delayDuration={delayDuration}>
        <TooltipPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
          <TooltipPrimitive.Trigger asChild>
            <Component
              ref={mergedRef}
              className={cn(truncationClass, lineClamp === 1 ? 'block' : 'min-w-0 w-full', className)}
              {...rest}
            >
              {children}
            </Component>
          </TooltipPrimitive.Trigger>

          <TooltipContent withinPortal={withinPortal} side={side} sideOffset={sideOffset} align={align}>
            {label ?? children}
          </TooltipContent>
        </TooltipPrimitive.Root>
      </TooltipProvider>
    );
  },
);

export const EllipsisTooltip = memo(EllipsisTooltipBase);
